export interface GachaAudioPort { prepare(): Promise<void>; unlock(): void; play(): void; stop(): void }

export class GachaAudioDirector implements GachaAudioPort {
  private buffer: AudioBuffer | null = null;
  private loading: Promise<void> | null = null;
  private source: AudioBufferSourceNode | null = null;
  private readonly gain: GainNode | null;
  private fallback: HTMLAudioElement | null = null;
  private volume = 0.72;
  private muted = false;
  private destroyed = false;
  constructor(private readonly context: AudioContext | null, destination: AudioNode | null) {
    this.gain = context?.createGain() ?? null;
    if (this.gain && destination) this.gain.connect(destination);
  }
  unlock(): void { if (this.context?.state === 'suspended') void this.context.resume().catch(() => {}); }
  setVolume(volume: number, muted: boolean): void {
    this.volume = volume; this.muted = muted;
    if (this.gain) this.gain.gain.value = muted ? 0 : volume;
    if (this.fallback) { this.fallback.volume = volume; this.fallback.muted = muted; }
  }
  prepare(): Promise<void> {
    if (this.loading) return this.loading;
    this.loading = (async () => {
      if (this.context) {
        const response = await fetch('/assets/audio/gacha/pull-original.mp3');
        if (!response.ok) throw new Error('Gacha audio unavailable');
        const buffer = await this.context.decodeAudioData(await response.arrayBuffer());
        if (!this.destroyed) this.buffer = buffer;
      } else {
        this.fallback = new Audio('/assets/audio/gacha/pull-original.mp3');
        this.fallback.preload = 'auto';
        this.fallback.load();
      }
    })().catch(() => { this.loading = null; });
    return this.loading;
  }
  play(): void {
    this.stop(); this.unlock();
    if (this.context && this.buffer && this.gain) {
      const source = this.context.createBufferSource();
      source.buffer = this.buffer;
      source.connect(this.gain);
      this.source = source;
      source.onended = () => { source.disconnect(); if (this.source === source) this.source = null; };
      this.setVolume(this.volume, this.muted);
      source.start();
    } else if (this.fallback) {
      this.setVolume(this.volume, this.muted);
      this.fallback.currentTime = 0;
      void this.fallback.play().catch(() => {});
    }
  }
  stop(): void {
    if (this.source) { this.source.stop(); this.source.disconnect(); this.source = null; }
    this.fallback?.pause();
  }
  destroy(): void { this.destroyed = true; this.stop(); this.gain?.disconnect(); this.fallback = null; }
}
